import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { Icon } from '../Icon/Icon.es.js';
import { ProgressBarStep } from './ProgressBar.Step/ProgressBar.Step.es.js';

const ProgressBar = ({ progress, variant = 'primary', maxValueLabel, currentValueLabel, errorValueLabel, withMark, preAnimate = false, barHeight = 'default', onStepComplete, children, className })=>{
    /**
     * Compute values based on 'children' prop.
     */ const computed = useMemo(()=>{
        let cProgress = progress;
        // Can't be negative
        if (progress < 0) {
            cProgress = 0;
        }
        // Can't exceed 100%
        if (progress > 100) {
            cProgress = 100;
        }
        // Can't exceed number of children
        if (children && progress > children.length) {
            cProgress = children.length;
        }
        const percentage = children ? 100 * cProgress / children.length : cProgress;
        return {
            progress,
            percentage
        };
    }, [
        children,
        progress
    ]);
    /**
     * Initialize current state with default 0 when `preAnimate`
     * is true, to kick in the animation on component mount.
     */ const [current, setCurrent] = useState(preAnimate ? {
        progress: 0,
        percentage: 0
    } : computed);
    /**
     * Update current state after one tick to preAnimate.
     */ useEffect(()=>{
        const timeout = setTimeout(()=>setCurrent(computed), 0);
        return ()=>clearTimeout(timeout);
    }, [
        computed
    ]);
    /**
     * On Transition End.
     */ const onTransitionEnd = useCallback(()=>{
        onStepComplete?.(current.progress);
    }, [
        current.progress,
        onStepComplete
    ]);
    /**
     * Render Nodes.
     */ const renderNode = (_, i)=>{
        const filled = i + 1 <= current.progress;
        const first = i === 0;
        const last = i + 1 === children?.length;
        return jsxs("div", {
            className: "container",
            children: [
                (first || last) && jsx("div", {
                    className: classNames('edge', {
                        last
                    })
                }),
                jsxs("div", {
                    className: classNames('node', {
                        filled
                    }),
                    role: filled ? 'filledNode' : 'emptyNode',
                    children: [
                        jsx(Icon, {
                            name: "CheckCircleSolid"
                        }),
                        jsx("div", {
                            className: "icon-background"
                        })
                    ]
                })
            ]
        }, i);
    };
    return jsxs("div", {
        className: classNames('cb-progress', className, {
            children
        }),
        children: [
            !children && maxValueLabel && jsx("span", {
                className: "max_label",
                "data-testid": "max-value",
                children: maxValueLabel
            }),
            jsxs("div", {
                className: classNames('progress', {
                    [barHeight]: barHeight
                }),
                "data-testid": "bar",
                children: [
                    withMark && jsx("span", {
                        className: "mark",
                        "data-testid": "mark",
                        style: {
                            left: `${current.percentage}%`
                        }
                    }),
                    jsx("div", {
                        className: classNames('filler', {
                            [variant]: variant
                        }),
                        "data-testid": "progress",
                        onTransitionEnd: onTransitionEnd,
                        style: {
                            width: `${current.percentage}%`
                        }
                    }),
                    !children && currentValueLabel && jsx("div", {
                        className: "current_label_parent",
                        children: jsx("span", {
                            className: classNames('current_label', {
                                middle: current.progress < 95 && current.progress > 5,
                                edge: current.progress >= 95
                            }),
                            "data-testid": "current-value",
                            style: {
                                left: `${current.percentage < 95 ? current.percentage : 100}%`
                            },
                            children: currentValueLabel
                        })
                    }),
                    errorValueLabel && jsx("span", {
                        className: "error_label",
                        "data-testid": "error-value",
                        children: errorValueLabel
                    })
                ]
            }),
            jsx("div", {
                className: "nodes",
                children: children && children.map(renderNode)
            }),
            jsx("div", {
                className: "descriptions",
                children: children
            })
        ]
    });
};
ProgressBar.Step = ProgressBarStep;
ProgressBar.displayName = 'ProgressBar';

export { ProgressBar, ProgressBar as default };
//# sourceMappingURL=ProgressBar.es.js.map
